import { useMemo, useId } from 'react';
import Chart from 'react-apexcharts';
import { Skeleton } from '../ui/Skeleton';
import { getDarkLineAreaChartBase, mergeChartOptions } from '../../utils/darkChartTheme';
import {
  CHART_TOOLBAR_ROW_CLASS,
  CHART_TOOLBAR_TOGGLE_GROUP_CLASS,
  chartToolbarToggleBtnClass,
} from './chartToolbarTheme';
import type { CodeCompositionByPeriod } from '../../types/api/metrics';

const PERIODS = [
  { id: 'weekly', label: 'Week' },
  { id: 'monthly', label: 'Month' },
] as const;

type ChartPeriod = (typeof PERIODS)[number]['id'];

type CodeCompositionChartProps = {
  compositionByPeriod: CodeCompositionByPeriod | null;
  period: ChartPeriod;
  onPeriodChange: (period: ChartPeriod) => void;
  loading?: boolean;
  height?: number;
};

function formatBucketLabel(isoDate: string, period: ChartPeriod): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  if (period === 'monthly') {
    return d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

export default function CodeCompositionChart({
  compositionByPeriod,
  period,
  onPeriodChange,
  loading = false,
  height = 240,
}: CodeCompositionChartProps) {
  const chartUid = useId().replace(/:/g, '');

  const bucketStarts = compositionByPeriod?.bucketStarts ?? [];
  const hasTrend = bucketStarts.length > 1;

  const categories = useMemo(
    () => bucketStarts.map((d) => formatBucketLabel(d, period)),
    [bucketStarts, period]
  );

  const series = useMemo(
    () => [
      {
        name: 'New files',
        data: (compositionByPeriod?.newFileRatios ?? []).map((v) => (v == null ? 0 : v)),
      },
      {
        name: 'Existing changes',
        data: (compositionByPeriod?.existingFileRatios ?? []).map((v) => (v == null ? 0 : v)),
      },
      {
        name: 'Rework',
        data: (compositionByPeriod?.reworkRatios ?? []).map((v) => (v == null ? 0 : v)),
      },
    ],
    [compositionByPeriod]
  );

  const chartOptions = useMemo(
    () =>
      mergeChartOptions(
        getDarkLineAreaChartBase({
          enableToolbar: false,
          chartType: 'bar',
        }),
        {
          chart: {
            id: `code-composition-${chartUid}`,
            stacked: true,
            stackType: '100%',
            animations: { enabled: true, easing: 'easeinout', speed: 400 },
          },
          plotOptions: {
            bar: { borderRadius: 2, columnWidth: '65%' },
          },
          colors: ['#10b981', '#6366f1', '#f59e0b'],
          xaxis: {
            categories,
            labels: {
              maxRotation: 45,
              hideOverlappingLabels: true,
            },
          },
          yaxis: {
            labels: { formatter: (v: number) => `${Math.round(Number(v))}%` },
            max: 100,
            min: 0,
          },
          legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'left',
          },
          tooltip: {
            y: {
              formatter: (v: number) => `${Math.round(Number(v) * 10) / 10}%`,
            },
          },
          grid: { xaxis: { lines: { show: false } } },
        }
      ),
    [categories, chartUid]
  );

  return (
    <div className="min-w-0">
      <div className={`${CHART_TOOLBAR_ROW_CLASS} justify-end`}>
        <fieldset className={`${CHART_TOOLBAR_TOGGLE_GROUP_CLASS} border-0 p-0 m-0 min-w-0`}>
          <legend className="sr-only">Chart period</legend>
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={chartToolbarToggleBtnClass(period === p.id)}
              aria-pressed={period === p.id}
              onClick={() => onPeriodChange(p.id)}
            >
              {p.label}
            </button>
          ))}
        </fieldset>
      </div>

      {loading ? (
        <Skeleton className="mt-3 h-[240px] w-full rounded-lg" />
      ) : !hasTrend ? (
        <p className="mt-3 text-sm text-slate-400 py-6 text-center border border-dashed border-dark-border rounded-lg">
          Not enough data for a trend in this window.
        </p>
      ) : (
        <div className="mt-2">
          <Chart type="bar" height={height} width="100%" series={series} options={chartOptions} />
        </div>
      )}
    </div>
  );
}
